/**
 * M7 S14: the first-hour hooks (spec §2.1–§2.2, §11).
 *
 * - **Read on pickup (§2.1, W6):** picking up a generic data core copies a log into Logs, unread. The core then sells
 *   normally (the Hub buys cores), and selling it never touches the ledger. Story items, sealed and contract cores
 *   are not generic.
 * - **The Harrow core (§2.2):** the first core salvaged at the Harrow carries the fixed `evacuation` log
 *   (`hooks.json firstHour.coreLog`). "At the Harrow" is the salvage site's POI matching `home.harrowPoiId`
 *   (`systemWorld.ts salvageSiteOf`), never a key literal: derelict keys carry the derelict seed (BUG-B1, D-345).
 * - **Any other core:** a log drawn deterministically from the core id (FNV-1a) among the authored logs of the
 *   core's source that the tier allows, probing past logs already held; none once they are all held.
 * - **The first ruin (§2.2):** the first First Rush ruin visited carries the fixed `settlement` fragment.
 * - **The mining-path hook (§2.2, W7):** the first ore receipt at the Refinery posts the Mutual Bulletin item about
 *   old convoy transponders near the Harrow, with Kell's tier-1 nudge; an ore sale anywhere posts it from the end
 *   of hour one, and `harrowHookAtTime` posts it at the end of hour one for a career that already sold ore elsewhere.
 *
 * Pure, Career-in, Career-out and event-driven (never per frame). Data is passed in so tests use their own.
 */
import type { Career, CargoComponent } from '../career/types'
import evacuationJson from '../data/story/evidence/evacuation.json'
import reportJson from '../data/story/evidence/report.json'
import settlementJson from '../data/story/evidence/settlement.json'
import watchJson from '../data/story/evidence/watch.json'
import hooksJson from '../data/story/hooks.json'
import { addLog, hasLog } from './logs'
import { evidenceFileSchema, hooksFileSchema, type EvidenceLog, type HooksConfig } from './schemas'
import { commsConfig, nudgeNodeId } from './comms'
import { hasFlag, setFlag, storyOf, withStory } from './state'
import type { LogSource, StoryCommand, StoryEffect, StoryFlag, StoryResult } from './types'

// ---------------------------------------------------------------------------------------------
// Config and data
// ---------------------------------------------------------------------------------------------

/** Parses `hooks.json` (throws naming the path, like the other story loaders). */
export function loadHooksConfig(raw: unknown): HooksConfig {
  const result = hooksFileSchema.safeParse(raw)
  if (result.success) return result.data
  const issue = result.error.issues[0]
  const where = issue && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)'
  throw new Error(`story data: hooks.json: ${where}: ${issue?.message ?? 'invalid'}`)
}

let cachedConfig: HooksConfig | undefined

/** The shipped hooks config, parsed once on first use. */
export function hooksConfig(): HooksConfig {
  cachedConfig ??= loadHooksConfig(hooksJson)
  return cachedConfig
}

/** What the hooks read: the config and the authored logs (the boot logs never drop from a generic core). */
export interface HookData {
  hooks: HooksConfig
  /** Every thread log, in file order (the deterministic pick depends on this order). */
  evidence: readonly EvidenceLog[]
  logsById: ReadonlyMap<string, EvidenceLog>
}

let cachedData: HookData | undefined

/** The shipped hook data: the four thread files, parsed once (no `storyData()`, so the career layer stays light). */
export function hookData(): HookData {
  if (cachedData !== undefined) return cachedData
  const evidence: EvidenceLog[] = []
  for (const raw of [evacuationJson, settlementJson, watchJson, reportJson]) evidence.push(...evidenceFileSchema.parse(raw).logs)
  cachedData = { hooks: hooksConfig(), evidence, logsById: new Map(evidence.map((l) => [l.id, l])) }
  return cachedData
}

// ---------------------------------------------------------------------------------------------
// Data cores (spec §2.1, §2.2)
// ---------------------------------------------------------------------------------------------

/** Where a core was picked up. */
export interface CoreOrigin {
  /** The salvage site's POI (`SalvageSite.poiId`), or null (a kill wreck, a pod). */
  poiId: string | null
  /** The home cluster's Harrow POI (`HomeCluster.harrowPoiId`), or null outside the home cluster's layout. */
  harrowPoiId: string | null
  /** The log source the core's log is drawn from (default: the first of `firstHour.coreSources`). */
  source?: LogSource
}

/** A core with no known site: a generic derelict-core log, never the Harrow's. */
export const NO_ORIGIN: CoreOrigin = Object.freeze({ poiId: null, harrowPoiId: null })

/** Whether `c` is a generic data core (not a story item, sealed or contract cargo). */
export function isGenericCore(c: Pick<CargoComponent, 'type' | 'story' | 'sealed' | 'contractId'>): boolean {
  return c.type === 'dataCore' && c.story !== true && c.sealed !== true && c.contractId === undefined
}

/** True when the origin is the Harrow POI (tested on the POI id, never on the core's key). */
export function atHarrow(origin: CoreOrigin): boolean {
  return origin.poiId !== null && origin.harrowPoiId !== null && origin.poiId === origin.harrowPoiId
}

/** FNV-1a over the id's UTF-16 code units: a stable 32-bit hash (no platform dependence). */
export function idHash(id: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h >>> 0
}

/** The tier as the hooks read it (a new career's 0 reads as tier 1). */
function tierOf(career: Career): number {
  return Math.max(1, career.storyTier ?? 0)
}

function poolFor(career: Career, source: LogSource, data: HookData): EvidenceLog[] {
  const fixed = data.hooks.firstHour
  const tier = tierOf(career)
  return data.evidence.filter(
    (l) =>
      l.source === source &&
      l.fromTier <= tier &&
      l.item === undefined &&
      !l.repeatable &&
      !l.template &&
      !l.outsideBudget &&
      l.id !== fixed.coreLog &&
      l.id !== fixed.ruinLog,
  )
}

/**
 * The log a generic core with id `coreId` copies, or null (nothing left to copy). The first core at the Harrow
 * gets the fixed log; otherwise the pick starts at `hash(coreId) mod pool` and probes past held logs.
 */
export function coreLogFor(career: Career, coreId: string, origin: CoreOrigin, data: HookData = hookData()): string | null {
  const fixed = data.hooks.firstHour.coreLog
  if (atHarrow(origin) && !hasLog(career, fixed) && data.logsById.has(fixed)) return fixed
  const source = origin.source ?? data.hooks.firstHour.coreSources[0]
  if (!data.hooks.firstHour.coreSources.includes(source)) return null
  const pool = poolFor(career, source, data)
  if (pool.length === 0) return null
  const start = idHash(coreId) % pool.length
  for (let i = 0; i < pool.length; i++) {
    const log = pool[(start + i) % pool.length]
    if (!hasLog(career, log.id)) return log.id
  }
  return null
}

/**
 * Copies the core's log into Logs, unread (spec §2.1). The fixed Harrow log also sets `first_core_logged`. The core
 * itself is untouched: selling it later keeps the log (W6).
 */
export function copyCoreLog(career: Career, coreId: string, origin: CoreOrigin, data: HookData = hookData()): Career {
  const logId = coreLogFor(career, coreId, origin, data)
  if (logId === null) return career
  const next = addLog(career, data.logsById, logId)
  return logId === data.hooks.firstHour.coreLog ? setFlag(next, 'first_core_logged') : next
}

// ---------------------------------------------------------------------------------------------
// The first ruin (spec §2.2)
// ---------------------------------------------------------------------------------------------

/** The first First Rush ruin visited carries the fixed `settlement` fragment; later visits add nothing here. */
export function ruinVisited(career: Career, data: HookData = hookData()): Career {
  if (hasFlag(career, 'first_ruin_logged')) return career
  const logId = data.hooks.firstHour.ruinLog
  return setFlag(addLog(career, data.logsById, logId), 'first_ruin_logged')
}

function NONE(career: Career): StoryResult<Career> {
  return { career, commands: [], effects: [] }
}

// ---------------------------------------------------------------------------------------------
// Kell's nudge (spec §11; the schedule and the hint ladder are `nudges.ts`)
// ---------------------------------------------------------------------------------------------

/** The `kell_nudge_<tier>` flags, tiers 1–4. */
export const NUDGE_FLAGS: readonly StoryFlag[] = ['kell_nudge_1', 'kell_nudge_2', 'kell_nudge_3', 'kell_nudge_4']

/** The flag marking `tier`'s nudge sent, or null for a tier without one. */
export function nudgeFlag(tier: number): StoryFlag | null {
  return NUDGE_FLAGS[tier - 1] ?? null
}

/** Kell won't talk at hostile Holdfast standing (spec §3.1: a comms option with a hostile faction is refused). */
export function kellSilent(career: Career, hostileStanding: number = commsConfig().contacts.hostileStanding): boolean {
  return career.standings.holdfast <= hostileStanding
}

/**
 * Sends Kell's one nudge for `tier` now (the flag marks it; never twice). Nothing when it was sent, the tier has no
 * nudge, or Kell won't talk.
 */
export function sendNudge(career: Career, tier: number): StoryResult<Career> {
  const flag = nudgeFlag(tier)
  if (flag === null || hasFlag(career, flag) || kellSilent(career)) return NONE(career)
  const now = storyOf(career).onlineDays
  const flagged = setFlag(career, flag)
  const next = withStory(flagged, (s) => ({
    ...s,
    nudge:
      s.nudge !== undefined && s.nudge.tier === tier
        ? { ...s.nudge, lastOnlineDay: now }
        : { tier, sinceOnlineDay: now, rungs: 0, lastOnlineDay: now },
  }))
  const effects: StoryEffect[] = [
    { type: 'comms', speaker: 'kell', nodeId: nudgeNodeId(tier) },
    { type: 'autosave', reason: 'kellNudge' },
  ]
  return { career: next, commands: [], effects }
}

// ---------------------------------------------------------------------------------------------
// The mining-path hook (spec §2.2, W7)
// ---------------------------------------------------------------------------------------------

/** Mining income booked in the career's income window (the ore sales `logging.ts` records). */
function soldOre(career: Career): boolean {
  const mix = career.incomeMix
  if (mix === undefined) return false
  for (const bucket of mix.buckets) if (bucket.mining > 0) return true
  return false
}

/** Posts the Bulletin item once (the flag marks it), with Kell's tier-1 nudge pointing at the same derelict. */
function postHarrowHook(career: Career, data: HookData): StoryResult<Career> {
  const h = data.hooks.harrowHook
  const commands: StoryCommand[] = [{ type: 'postWire', headline: h.headline, severity: h.severity, source: h.source }]
  const posted = setFlag(career, 'harrow_hook_posted')
  const nudge = tierOf(posted) === 1 ? sendNudge(posted, 1) : NONE(posted)
  const effects: StoryEffect[] = [...nudge.effects.filter((e) => e.type !== 'autosave'), { type: 'autosave', reason: 'harrowHook' }]
  return { career: nudge.career, commands, effects }
}

/**
 * After an ore sale at M2 station `stationId` (call it with the sale's Career): the Refinery receipt posts the item
 * at once; a sale anywhere else posts it from the end of hour one (`hourOneS` of game time).
 */
export function harrowHookOnOreSale(career: Career, stationId: string, data: HookData = hookData()): StoryResult<Career> {
  if (hasFlag(career, 'harrow_hook_posted')) return NONE(career)
  const h = data.hooks.harrowHook
  if (h.receiptStations.includes(stationId) || career.gameTime >= h.hourOneS) return postHarrowHook(career, data)
  return NONE(career)
}

/** At the end of hour one (call it on any later game-time event): posts the item if ore was sold anywhere. */
export function harrowHookAtTime(career: Career, data: HookData = hookData()): StoryResult<Career> {
  if (hasFlag(career, 'harrow_hook_posted') || career.gameTime < data.hooks.harrowHook.hourOneS) return NONE(career)
  return soldOre(career) ? postHarrowHook(career, data) : NONE(career)
}

/** Whether the Bulletin item has been posted (the story's own record of it). */
export function harrowHookPosted(career: Career): boolean {
  return storyOf(career).flags.harrow_hook_posted === true
}
